import { useState } from "react";
import { motion } from "framer-motion";
import { FILTERS } from "@/lib/filters";
import { fadeUp } from "@/lib/motion";

const DEMO_IMG =
    "https://images.pexels.com/photos/7610521/pexels-photo-7610521.jpeg?auto=compress&cs=tinysrgb&w=1200";

export default function FilterShowcase() {
    const [active, setActive] = useState(FILTERS[1]);

    return (
        <section id="filters" className="relative py-20 sm:py-28 lg:py-32">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <motion.div {...fadeUp} className="max-w-2xl">
                    <p className="mb-4 font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-amberx">
                        The Filter Wall
                    </p>
                    <h2 className="font-display text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl">
                        One tap. Whole <span className="text-amberx">mood.</span>
                    </h2>
                    <p className="mt-4 text-base leading-relaxed text-slate-400 sm:text-lg">
                        Sixteen hand-tuned grades, built for phone footage. Try them right here —
                        this is exactly how they behave inside the editor.
                    </p>
                </motion.div>

                <motion.div
                    {...fadeUp}
                    transition={{ ...fadeUp.transition, delay: 0.1 }}
                    className="mt-10 grid gap-6 lg:grid-cols-12"
                >
                    <div className="lg:col-span-8">
                        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-surface">
                            <div className="aspect-[16/10] w-full">
                                <img
                                    src={DEMO_IMG}
                                    alt="Skater mid-trick, used to preview Deoeditz filters"
                                    className="h-full w-full object-cover transition-[filter] duration-500"
                                    style={{ filter: active.css || "none" }}
                                    loading="lazy"
                                />
                            </div>
                            <div className="absolute left-4 top-4 rounded-full bg-obsidian/75 px-4 py-2 font-mono text-[10px] font-bold tracking-[0.2em] text-amberx backdrop-blur-sm">
                                {active.name.toUpperCase()}
                            </div>
                            <div className="absolute bottom-4 right-4 rounded-full bg-obsidian/75 px-4 py-2 font-mono text-[10px] font-bold tracking-[0.2em] text-slate-300 backdrop-blur-sm">
                                00:00:14:22
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-4">
                        <div className="grid grid-cols-2 gap-3 lg:grid-cols-2">
                            {FILTERS.map((f, i) => {
                                const selected = active.id === f.id;
                                return (
                                    <motion.button
                                        key={f.id}
                                        data-testid={`filter-demo-${f.id}`}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, amount: 0.2 }}
                                        transition={{ duration: 0.5, delay: i * 0.04, ease: [0.16, 1, 0.3, 1] }}
                                        onClick={() => setActive(f)}
                                        className={`group relative h-24 overflow-hidden rounded-2xl border text-left transition-all sm:h-28 ${
                                            selected
                                                ? "border-amberx shadow-[0_0_24px_rgba(255,159,28,0.25)]"
                                                : "border-white/10 hover:border-white/30"
                                        }`}
                                    >
                                        <img
                                            src={DEMO_IMG}
                                            alt=""
                                            aria-hidden="true"
                                            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                            style={{ filter: f.css || "none" }}
                                            loading="lazy"
                                        />
                                        <span
                                            className={`absolute inset-0 ${
                                                selected ? "bg-obsidian/35" : "bg-obsidian/55 group-hover:bg-obsidian/35"
                                            } transition-colors`}
                                        />
                                        <span className="absolute bottom-2.5 left-3 font-display text-xs font-bold tracking-wide text-slate-50">
                                            {f.name}
                                        </span>
                                        {selected && (
                                            <span className="absolute right-2.5 top-2.5 h-2.5 w-2.5 rounded-full bg-amberx shadow-[0_0_12px_rgba(255,159,28,0.9)]" />
                                        )}
                                    </motion.button>
                                );
                            })}
                        </div>
                        <p className="mt-4 font-mono text-[10px] leading-relaxed tracking-wider text-slate-500">
                            INTENSITY, FINE-TUNE AND BEFORE/AFTERS LIVE INSIDE THE{" "}
                            <a href="/editor" className="text-amberx underline underline-offset-4">
                                EDITOR
                            </a>
                            .
                        </p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
